// SNIGJ - Trial Management Page (Gestion des Procès)
// For OPJ, Tribunals, and Prosecutors - Full case workflow management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import PersonSearchCombo from '../components/shared/PersonSearchCombo';
import FileUploader from '../components/shared/FileUploader';
import RichTextEditor from '../components/shared/RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { 
  Gavel, FileText, Users, Plus, Search, Filter, Eye, Edit, Clock, CheckCircle,
  AlertTriangle, Scale, UserCheck, FileCheck, Calendar, Building2, Shield,
  MessageSquare, Briefcase, ArrowRight, Download, Printer, RefreshCw, Upload, Hash, User, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Trial phases
const TRIAL_PHASES = [
  { value: 'DEPOSITION', label: { fr: 'Déposition', en: 'Deposition' }, icon: MessageSquare, color: 'bg-blue-500' },
  { value: 'CONFRONTATION', label: { fr: 'Confrontation', en: 'Confrontation' }, icon: Users, color: 'bg-orange-500' },
  { value: 'OPJ_ANALYSIS', label: { fr: 'Analyse OPJ', en: 'OPJ Analysis' }, icon: Shield, color: 'bg-purple-500' },
  { value: 'PROSECUTOR_REVIEW', label: { fr: 'Examen Parquet', en: 'Prosecutor Review' }, icon: Scale, color: 'bg-cyan-500' },
  { value: 'MAGISTRATE_DECISION', label: { fr: 'Décision Magistrat', en: 'Magistrate Decision' }, icon: Gavel, color: 'bg-amber-500' },
  { value: 'JUDGMENT', label: { fr: 'Jugement', en: 'Judgment' }, icon: FileCheck, color: 'bg-green-500' },
  { value: 'CLOSED', label: { fr: 'Clôturé', en: 'Closed' }, icon: CheckCircle, color: 'bg-gray-500' }
];

// Document types that can be generated
const DOCUMENT_TYPES = [
  { value: 'PV_DEPOSITION', label: { fr: 'PV de Déposition', en: 'Deposition Report' } },
  { value: 'PV_CONFRONTATION', label: { fr: 'PV de Confrontation', en: 'Confrontation Report' } },
  { value: 'RAPPORT_OPJ', label: { fr: 'Rapport OPJ', en: 'OPJ Report' } },
  { value: 'REQUISITOIRE', label: { fr: 'Réquisitoire', en: 'Prosecutor Request' } },
  { value: 'ORDONNANCE', label: { fr: 'Ordonnance', en: 'Court Order' } },
  { value: 'JUGEMENT', label: { fr: 'Jugement', en: 'Judgment' } },
  { value: 'COMPTE_RENDU', label: { fr: 'Compte Rendu', en: 'Report' } }
];

// Offense categories with sanctions
const OFFENSE_CATEGORIES = [
  { 
    value: 'VOL_SIMPLE', 
    label: { fr: 'Vol simple', en: 'Simple theft' },
    minSanction: '1 mois', maxSanction: '5 ans', fine: '10-500 USD'
  },
  { 
    value: 'VOL_AGGRAVE', 
    label: { fr: 'Vol aggravé', en: 'Aggravated theft' },
    minSanction: '5 ans', maxSanction: '20 ans', fine: '100-5000 USD'
  },
  { 
    value: 'COUPS_BLESSURES', 
    label: { fr: 'Coups et blessures', en: 'Assault and battery' },
    minSanction: '8 jours', maxSanction: '2 ans', fine: '50-200 USD'
  },
  { 
    value: 'COUPS_BLESSURES_GRAVES', 
    label: { fr: 'Coups et blessures graves', en: 'Serious assault' },
    minSanction: '2 ans', maxSanction: '10 ans', fine: '200-2000 USD'
  },
  { 
    value: 'MEURTRE', 
    label: { fr: 'Meurtre', en: 'Murder' },
    minSanction: '10 ans', maxSanction: 'Perpétuité', fine: 'N/A'
  },
  { 
    value: 'ASSASSINAT', 
    label: { fr: 'Assassinat', en: 'Premeditated murder' },
    minSanction: '20 ans', maxSanction: 'Peine de mort', fine: 'N/A'
  },
  { 
    value: 'ESCROQUERIE', 
    label: { fr: 'Escroquerie', en: 'Fraud' },
    minSanction: '3 mois', maxSanction: '5 ans', fine: '100-1000 USD'
  },
  { 
    value: 'ABUS_CONFIANCE', 
    label: { fr: 'Abus de confiance', en: 'Breach of trust' },
    minSanction: '6 mois', maxSanction: '5 ans', fine: '200-2000 USD'
  },
  { 
    value: 'CORRUPTION', 
    label: { fr: 'Corruption', en: 'Corruption' },
    minSanction: '1 an', maxSanction: '10 ans', fine: '500-10000 USD'
  },
  { 
    value: 'DETOURNEMENT', 
    label: { fr: 'Détournement de fonds', en: 'Embezzlement' },
    minSanction: '2 ans', maxSanction: '20 ans', fine: '1000-50000 USD'
  },
  { 
    value: 'VIOL', 
    label: { fr: 'Viol', en: 'Rape' },
    minSanction: '5 ans', maxSanction: '20 ans', fine: 'N/A'
  },
  { 
    value: 'TRAFIC_DROGUE', 
    label: { fr: 'Trafic de stupéfiants', en: 'Drug trafficking' },
    minSanction: '5 ans', maxSanction: '20 ans', fine: '5000-100000 USD'
  },
  { 
    value: 'REBELLION', 
    label: { fr: 'Rébellion', en: 'Rebellion' },
    minSanction: '1 an', maxSanction: '5 ans', fine: '100-1000 USD'
  },
  { 
    value: 'OUTRAGE', 
    label: { fr: 'Outrage à magistrat', en: 'Contempt of court' },
    minSanction: '15 jours', maxSanction: '1 an', fine: '50-500 USD'
  }
];

const TrialManagementPage = () => {
  const { user, language } = useAuth();
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trialAttachments, setTrialAttachments] = useState([]);

  // New trial form
  const [newTrial, setNewTrial] = useState({
    case_number: '',
    defendant_name: '',
    defendant_id: '',
    offense: '',
    offense_details: '',
    arrest_date: '',
    location: ''
  });

  // Handle defendant selection from PersonSearchCombo
  const handleDefendantSelect = (person) => {
    if (person) {
      setNewTrial(prev => ({
        ...prev,
        defendant_id: person.user_id || person.id,
        defendant_name: person.name || `${person.first_name} ${person.last_name}`
      }));
    } else {
      setNewTrial(prev => ({
        ...prev,
        defendant_id: '',
        defendant_name: ''
      }));
    }
  };

  // Phase action form
  const [phaseAction, setPhaseAction] = useState({
    phase: '',
    content: '',
    decision: '',
    next_hearing: ''
  });

  // Document form
  const [documentForm, setDocumentForm] = useState({
    type: '',
    title: '',
    content: ''
  });

  useEffect(() => {
    loadTrials();
  }, []);

  const loadTrials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trials');
      setTrials(response.data || []);
    } catch (error) {
      console.error('Failed to load trials:', error);
      // Mock data for demo
      setTrials([
        {
          trial_id: 'trial_001',
          case_number: 'RPP/2026/001',
          defendant_name: 'Jean Mukeba',
          offense: 'VOL_AGGRAVE',
          current_phase: 'OPJ_ANALYSIS',
          phase_progress: 43,
          created_at: '2026-01-15',
          next_hearing: '2026-02-10',
          status: 'IN_PROGRESS'
        },
        {
          trial_id: 'trial_002',
          case_number: 'RPP/2026/002',
          defendant_name: 'Marie Kalume',
          offense: 'ESCROQUERIE',
          current_phase: 'PROSECUTOR_REVIEW',
          phase_progress: 57,
          created_at: '2026-01-20',
          next_hearing: '2026-02-15',
          status: 'IN_PROGRESS'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrial = async () => {
    try {
      if (!newTrial.defendant_name || !newTrial.offense) {
        toast.error(language === 'fr' ? 'Veuillez remplir les champs obligatoires' : 'Please fill required fields');
        return;
      }

      setIsSubmitting(true);
      
      await api.post('/trials', {
        ...newTrial,
        attachments: trialAttachments.map(f => f.file_id),
        created_by: user.user_id,
        current_phase: 'DEPOSITION',
        status: 'IN_PROGRESS'
      });

      toast.success(language === 'fr' ? 'Procès créé avec succès' : 'Trial created successfully');
      setShowCreateModal(false);
      setNewTrial({
        case_number: '',
        defendant_name: '',
        defendant_id: '',
        offense: '',
        offense_details: '',
        arrest_date: '',
        location: ''
      });
      setTrialAttachments([]);
      loadTrials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhaseAction = async () => {
    try {
      if (!phaseAction.content) {
        toast.error(language === 'fr' ? 'Contenu requis' : 'Content required');
        return;
      }

      await api.post(`/trials/${selectedTrial.trial_id}/phases`, {
        ...phaseAction,
        created_by: user.user_id
      });

      toast.success(language === 'fr' ? 'Action enregistrée' : 'Action recorded');
      setShowPhaseModal(false);
      loadTrials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleGenerateDocument = async () => {
    try {
      if (!documentForm.type || !documentForm.content) {
        toast.error(language === 'fr' ? 'Type et contenu requis' : 'Type and content required');
        return;
      }

      await api.post(`/trials/${selectedTrial.trial_id}/documents`, {
        ...documentForm,
        created_by: user.user_id
      });

      toast.success(language === 'fr' ? 'Document généré' : 'Document generated');
      setShowDocumentModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getPhaseInfo = (phase) => TRIAL_PHASES.find(p => p.value === phase) || TRIAL_PHASES[0];
  const getOffenseInfo = (offense) => OFFENSE_CATEGORIES.find(o => o.value === offense);

  const getPhaseProgress = (currentPhase) => {
    const phaseIndex = TRIAL_PHASES.findIndex(p => p.value === currentPhase);
    return Math.round(((phaseIndex + 1) / TRIAL_PHASES.length) * 100);
  };

  // Filter trials
  const filteredTrials = trials.filter(trial => {
    const matchesSearch = !searchQuery || 
      trial.defendant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trial.case_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhase = filterPhase === 'all' || trial.current_phase === filterPhase;
    return matchesSearch && matchesPhase;
  });

  // Check if user can manage trials
  const canManageTrials = [
    'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL',
    'JUGE_PAIX', 'PRESIDENT_TRIBUNAL_PAIX', 'GREFFIER_PAIX',
    'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE', 'GREFFIER_GI',
    'CONSEILLER_APPEL', 'PRESIDENT_COUR_APPEL', 'GREFFIER_APPEL',
    'AVOCAT_GENERAL', 'PROCUREUR_GENERAL',
    'MAGISTRATE', 'JUDICIAL_POLICE_OFFICER',
    'NATIONAL_ADMIN', 'SUPER_ADMIN'
  ].includes(user?.role);

  if (!canManageTrials) {
    return (
      <DashboardLayout>
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'fr' ? 'Accès Restreint' : 'Restricted Access'}
            </h2>
            <p className="text-muted-foreground">
              {language === 'fr' 
                ? "Vous n'avez pas les permissions pour accéder à ce module."
                : "You don't have permissions to access this module."}
            </p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="trial-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Gavel className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Gestion des Procès' : 'Trial Management'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Suivi complet des affaires judiciaires'
                : 'Complete judicial case tracking'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={loadTrials}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              {language === 'fr' ? 'Nouveau Procès' : 'New Trial'}
            </Button>
          </div>
        </div>

        {/* Phase Legend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{language === 'fr' ? 'Phases du procès' : 'Trial Phases'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {TRIAL_PHASES.map((phase) => (
                <Badge key={phase.value} className={`${phase.color} text-white gap-1`}>
                  <phase.icon className="w-3 h-3" />
                  {phase.label[language]}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                    className="pl-10"
                    data-testid="trial-search"
                  />
                </div>
              </div>
              <Select value={filterPhase} onValueChange={setFilterPhase}>
                <SelectTrigger className="w-[200px]" data-testid="phase-filter">
                  <SelectValue placeholder={language === 'fr' ? 'Phase' : 'Phase'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes les phases' : 'All phases'}</SelectItem>
                  {TRIAL_PHASES.map((phase) => (
                    <SelectItem key={phase.value} value={phase.value}>{phase.label[language]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Trials List */}
        <div className="grid gap-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-muted rounded" />
                </CardContent>
              </Card>
            ))
          ) : filteredTrials.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Gavel className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Aucun procès trouvé' : 'No trials found'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredTrials.map((trial) => {
              const phaseInfo = getPhaseInfo(trial.current_phase);
              const offenseInfo = getOffenseInfo(trial.offense);
              const progress = getPhaseProgress(trial.current_phase);
              
              return (
                <Card key={trial.trial_id} className="hover:shadow-lg transition-shadow" data-testid={`trial-${trial.trial_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-lg">{trial.defendant_name}</h3>
                          <Badge variant="outline" className="font-mono">{trial.case_number}</Badge>
                          <Badge className={`${phaseInfo.color} text-white gap-1`}>
                            <phaseInfo.icon className="w-3 h-3" />
                            {phaseInfo.label[language]}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Infraction' : 'Offense'}</p>
                            <p className="font-medium">{offenseInfo?.label[language] || trial.offense}</p>
                          </div>
                          {offenseInfo && (
                            <div>
                              <p className="text-muted-foreground">{language === 'fr' ? 'Sanction prévue' : 'Expected sanction'}</p>
                              <p className="font-medium">{offenseInfo.minSanction} - {offenseInfo.maxSanction}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Prochaine audience' : 'Next hearing'}</p>
                            <p className="font-medium">{trial.next_hearing || '—'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Créé le' : 'Created on'}</p>
                            <p className="font-medium">{trial.created_at}</p>
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{language === 'fr' ? 'Progression' : 'Progress'}</span>
                            <span className="font-medium">{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 ml-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedTrial(trial); setShowPhaseModal(true); }}
                          data-testid={`phase-action-${trial.trial_id}`}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          {language === 'fr' ? 'Action' : 'Action'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedTrial(trial); setShowDocumentModal(true); }}
                          data-testid={`generate-doc-${trial.trial_id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {language === 'fr' ? 'Document' : 'Document'}
                        </Button>
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Trial Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gavel className="w-5 h-5" />
                {language === 'fr' ? 'Nouveau Procès' : 'New Trial'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    {language === 'fr' ? 'N° Dossier' : 'Case Number'}
                  </Label>
                  <Input 
                    value={newTrial.case_number}
                    onChange={(e) => setNewTrial({...newTrial, case_number: e.target.value})}
                    placeholder={language === 'fr' ? "Auto-généré si vide" : "Auto-generated if empty"}
                    data-testid="new-trial-case-number"
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Laissez vide pour numérotation automatique' : 'Leave empty for auto-numbering'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Date d\'arrestation' : 'Arrest Date'}</Label>
                  <Input 
                    type="date"
                    value={newTrial.arrest_date}
                    onChange={(e) => setNewTrial({...newTrial, arrest_date: e.target.value})}
                    data-testid="new-trial-arrest-date"
                  />
                </div>
              </div>

              {/* Defendant Selection with PersonSearchCombo */}
              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {language === 'fr' ? 'Prévenu' : 'Defendant'}
                </h4>
                <PersonSearchCombo
                  label={language === 'fr' ? 'Rechercher le prévenu *' : 'Search defendant *'}
                  placeholder={language === 'fr' ? 'Tapez le nom ou UNJI...' : 'Type name or UNJI...'}
                  onPersonSelect={handleDefendantSelect}
                  showCreateOption={true}
                  required={true}
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Infraction / Délit *' : 'Offense *'}</Label>
                <Select 
                  value={newTrial.offense}
                  onValueChange={(v) => setNewTrial({...newTrial, offense: v})}
                >
                  <SelectTrigger data-testid="new-trial-offense">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner l\'infraction' : 'Select offense'} />
                  </SelectTrigger>
                  <SelectContent>
                    {OFFENSE_CATEGORIES.map((offense) => (
                      <SelectItem key={offense.value} value={offense.value}>
                        <div className="flex items-center justify-between w-full">
                          <span>{offense.label[language]}</span>
                          <span className="text-xs text-muted-foreground ml-4">
                            {offense.minSanction} - {offense.maxSanction}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newTrial.offense && (
                <Card className="bg-accent/50">
                  <CardContent className="p-4">
                    <p className="text-sm font-medium mb-2">{language === 'fr' ? 'Sanctions prévues par la loi:' : 'Legal sanctions:'}</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">{language === 'fr' ? 'Minimum' : 'Minimum'}</p>
                        <p className="font-bold">{getOffenseInfo(newTrial.offense)?.minSanction}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{language === 'fr' ? 'Maximum' : 'Maximum'}</p>
                        <p className="font-bold">{getOffenseInfo(newTrial.offense)?.maxSanction}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{language === 'fr' ? 'Amende' : 'Fine'}</p>
                        <p className="font-bold">{getOffenseInfo(newTrial.offense)?.fine}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Détails de l\'infraction' : 'Offense Details'}</Label>
                <Textarea 
                  value={newTrial.offense_details}
                  onChange={(e) => setNewTrial({...newTrial, offense_details: e.target.value})}
                  placeholder={language === 'fr' ? 'Description des faits...' : 'Description of facts...'}
                  rows={3}
                  data-testid="new-trial-details"
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Lieu de l\'infraction' : 'Location'}</Label>
                <Input 
                  value={newTrial.location}
                  onChange={(e) => setNewTrial({...newTrial, location: e.target.value})}
                  placeholder={language === 'fr' ? 'Adresse ou commune' : 'Address or commune'}
                  data-testid="new-trial-location"
                />
              </div>

              {/* File Attachments Section */}
              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  {language === 'fr' ? 'Pièces jointes' : 'Attachments'}
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' 
                    ? 'Documents, photos, audio ou vidéo liés au procès'
                    : 'Documents, photos, audio or video related to the trial'}
                </p>
                <FileUploader
                  value={trialAttachments}
                  onChange={setTrialAttachments}
                  entityType="trial"
                  maxFiles={20}
                  maxFileSize={100 * 1024 * 1024}
                  acceptedTypes="image/*,video/*,audio/*,.pdf,.doc,.docx"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleCreateTrial} disabled={isSubmitting} className="gap-2">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {language === 'fr' ? 'Créer le procès' : 'Create Trial'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase Action Modal */}
        <Dialog open={showPhaseModal} onOpenChange={setShowPhaseModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5" />
                {language === 'fr' ? 'Action sur le procès' : 'Trial Action'}
                {selectedTrial && (
                  <Badge variant="outline" className="ml-2">{selectedTrial.case_number}</Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Phase actuelle' : 'Current Phase'}</Label>
                <div className="flex items-center gap-2">
                  {selectedTrial && (
                    <>
                      <Badge className={`${getPhaseInfo(selectedTrial.current_phase).color} text-white`}>
                        {getPhaseInfo(selectedTrial.current_phase).label[language]}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </>
                  )}
                  <Select 
                    value={phaseAction.phase}
                    onValueChange={(v) => setPhaseAction({...phaseAction, phase: v})}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder={language === 'fr' ? 'Phase suivante' : 'Next phase'} />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIAL_PHASES.map((phase) => (
                        <SelectItem key={phase.value} value={phase.value}>{phase.label[language]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Contenu / Observations *' : 'Content / Observations *'}</Label>
                <Textarea 
                  value={phaseAction.content}
                  onChange={(e) => setPhaseAction({...phaseAction, content: e.target.value})}
                  placeholder={language === 'fr' ? 'Saisissez le contenu de la déposition, analyse, décision...' : 'Enter deposition content, analysis, decision...'}
                  rows={6}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Décision' : 'Decision'}</Label>
                  <Input 
                    value={phaseAction.decision}
                    onChange={(e) => setPhaseAction({...phaseAction, decision: e.target.value})}
                    placeholder={language === 'fr' ? 'Renvoi, Acquittement, Condamnation...' : 'Dismissal, Acquittal, Conviction...'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Prochaine audience' : 'Next Hearing'}</Label>
                  <Input 
                    type="date"
                    value={phaseAction.next_hearing}
                    onChange={(e) => setPhaseAction({...phaseAction, next_hearing: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPhaseModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handlePhaseAction} className="gap-2">
                <CheckCircle className="w-4 h-4" />
                {language === 'fr' ? 'Enregistrer' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate Document Modal */}
        <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {language === 'fr' ? 'Générer un document' : 'Generate Document'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de document *' : 'Document Type *'}</Label>
                <Select 
                  value={documentForm.type}
                  onValueChange={(v) => setDocumentForm({...documentForm, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner le type' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((doc) => (
                      <SelectItem key={doc.value} value={doc.value}>{doc.label[language]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre du document' : 'Document Title'}</Label>
                <Input 
                  value={documentForm.title}
                  onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})}
                  placeholder={language === 'fr' ? 'Titre...' : 'Title...'}
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Contenu *' : 'Content *'}</Label>
                <Textarea 
                  value={documentForm.content}
                  onChange={(e) => setDocumentForm({...documentForm, content: e.target.value})}
                  placeholder={language === 'fr' ? 'Saisissez le contenu du document...' : 'Enter document content...'}
                  rows={10}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocumentModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleGenerateDocument} className="gap-2">
                <Download className="w-4 h-4" />
                {language === 'fr' ? 'Générer' : 'Generate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TrialManagementPage;
